import React, { useCallback, useMemo, useState } from 'react';

import { faFileText, faMicrophone, faNoteSticky, faPlus } from '@fortawesome/pro-solid-svg-icons';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { FileModel, TagModel } from 'models';

import { CreateTextNoteRequest } from 'core/api/client/NotesClient';
import { NotificationsService } from 'core/notifications';
import { Accordion, Button, ButtonVariants, ComponentSizes, Icon, Input, TextArea } from 'core/ui';

import { useMutationNotes } from 'features/api/hooks/useMutationNotes';
import { TagsField } from 'features/tags/fragments/TagsField';

// -----------------------------------
// Props Interface
// -----------------------------------
interface FileNotesSectionProps {
  currentFile: FileModel;
  allFiles: FileModel[]; // All files in context to find related notes
  patientId: number;
  availableTags: TagModel[];
  areTagsLoading?: boolean;
  onNoteCreated?: (noteData: CreateTextNoteRequest & { fileId: number }) => void;
}

// -----------------------------------
// Styled Components
// -----------------------------------
const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
  padding: ${({ theme }) => theme.space.spacing10};
`;

const NotesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.space.spacing10};
`;

const NotesTitle = styled.h5`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const AddNoteButton = styled(Button)`
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing10};
  font-size: 0.8rem;
`;

const ExistingNotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
  max-height: 300px;
  overflow-y: auto;
`;

const NoteItem = styled.div`
  background: ${({ theme }) => theme.colors.palette.grayscale[0]};
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[2]};
  border-radius: ${({ theme }) => theme.radii.scale[1]};
  padding: ${({ theme }) => theme.space.spacing10};
  font-size: 0.85rem;
`;

const NoteHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};
  margin-bottom: ${({ theme }) => theme.space.spacing10};
`;

const NoteIcon = styled.div<{ $noteType: 'text' | 'audio' | 'ai' }>`
  color: ${({ $noteType, theme }) => ($noteType === 'text' ? theme.colors.primary : $noteType === 'audio' ? theme.colors.warning : theme.colors.success)};
`;

const NoteFileName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  flex: 1;
`;

const NoteDate = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const NoteContent = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
  margin-top: ${({ theme }) => theme.space.spacing10};
`;

const CreateNoteForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
  background: ${({ theme }) => theme.colors.palette.grayscale[0]};
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[2]};
  border-radius: ${({ theme }) => theme.radii.scale[1]};
  padding: ${({ theme }) => theme.space.spacing10};
`;

const FormActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing10};
  justify-content: flex-end;
`;

// -----------------------------------
// Main Component
// -----------------------------------
export const FileNotesSection: React.FC<FileNotesSectionProps> = ({
  currentFile,
  allFiles,
  patientId,
  availableTags,
  areTagsLoading = false,
  onNoteCreated,
}) => {
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteTitle, setNoteTitle] = useState('');
  const [selectedTags, setSelectedTags] = useState<TagModel[]>([]);

  const { createTextNoteMutation } = useMutationNotes(patientId);

  // Find related notes for this file based on naming patterns and relationships
  const relatedNotes = useMemo(() => {
    if (!currentFile || !allFiles) return [];

    const fileBaseName = currentFile.fileName?.replace(/\.[^/.]+$/, '') || '';
    const fileId = currentFile.id;

    return allFiles
      .filter((file) => {
        // Text notes typically contain "note" in filename and are text/plain
        const isTextNote = file.fileName?.toLowerCase().includes('note') && file.fileType?.toLowerCase() === 'text/plain';

        // Audio notes are audio files with note-related names
        const isAudioNote = file.fileType?.startsWith('audio/') && file.fileName?.toLowerCase().includes('note');

        // AI summaries and reports
        const isAISummary =
          file.fileName?.toLowerCase().includes('ai summary') ||
          file.fileName?.toLowerCase().includes('aisummary') ||
          file.description?.toLowerCase().includes('ai summary');

        // Check if note is related to current file by naming pattern or exam relationship
        const isRelatedByName = file.fileName?.includes(fileBaseName) || fileBaseName.includes(file.fileName?.replace(/\.[^/.]+$/, '') || '');
        const isRelatedByExam = file.examId && currentFile.examId && file.examId === currentFile.examId;

        return (isTextNote || isAudioNote || isAISummary) && (isRelatedByName || isRelatedByExam || file.id === fileId);
      })
      .sort((a, b) => {
        // Sort by creation date, newest first
        const dateA = dayjs(a.created || 0);
        const dateB = dayjs(b.created || 0);
        return dateB.valueOf() - dateA.valueOf();
      });
  }, [currentFile, allFiles]);

  const handleCreateNote = useCallback(async () => {
    if (!noteContent.trim()) {
      NotificationsService.displayError('Note content is required');
      return;
    }

    try {
      const noteData: CreateTextNoteRequest = {
        patientId,
        examId: currentFile.examId ?? undefined,
        content: noteContent,
        title: noteTitle || `Note for ${currentFile.fileName}`,
        description: `Note related to file: ${currentFile.fileName}`,
        tagIds: selectedTags.map((tag) => tag.id),
        categoryId: currentFile.categoryId ?? undefined,
        captureTime: new Date(),
        eventTime: new Date(),
      };

      const response = await createTextNoteMutation.mutateAsync(noteData);

      // Reset form
      setNoteContent('');
      setNoteTitle('');
      setSelectedTags([]);
      setIsCreatingNote(false);

      // Notify parent
      onNoteCreated?.({
        ...noteData,
        fileId: response.fileId,
      });

      NotificationsService.displaySuccess('Note created successfully');
    } catch (error) {
      console.error('Failed to create note:', error);
      NotificationsService.displayError('Failed to create note');
    }
  }, [noteContent, noteTitle, selectedTags, patientId, currentFile, createTextNoteMutation, onNoteCreated]);

  const getNoteTypeIcon = (file: FileModel) => {
    if (file.fileType?.startsWith('audio/')) {
      return <Icon icon={faMicrophone} />;
    }
    if (file.fileName?.toLowerCase().includes('ai summary')) {
      return <Icon icon={faFileText} />;
    }
    return <Icon icon={faNoteSticky} />;
  };

  const getNoteType = (file: FileModel): 'text' | 'audio' | 'ai' => {
    if (file.fileType?.startsWith('audio/')) return 'audio';
    if (file.fileName?.toLowerCase().includes('ai summary')) return 'ai';
    return 'text';
  };

  // Don't render the notes section if there are no related notes
  if (relatedNotes.length === 0) {
    return null;
  }

  return (
    <Accordion title={'Notes (' + relatedNotes.length + ')'}>
      <NotesContainer>
        <NotesHeader>
          <AddNoteButton variant={ButtonVariants.PRIMARY} size={ComponentSizes.SMALL} onClick={() => setIsCreatingNote(true)} disabled={isCreatingNote}>
            <Icon icon={faPlus} style={{ marginRight: '0.5rem' }} />
            Add Note
          </AddNoteButton>
        </NotesHeader>

        {isCreatingNote && (
          <CreateNoteForm
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateNote();
            }}
          >
            <Input placeholder="Note title (optional)" value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} />
            <TextArea placeholder="Enter your note content..." value={noteContent} onChange={(e) => setNoteContent(e.target.value || '')} rows={4} required />
            <TagsField tags={selectedTags} onChange={setSelectedTags} />
            <FormActions>
              <Button
                type="button"
                variant={ButtonVariants.SECONDARY}
                size={ComponentSizes.SMALL}
                onClick={() => {
                  setIsCreatingNote(false);
                  setNoteContent('');
                  setNoteTitle('');
                  setSelectedTags([]);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant={ButtonVariants.PRIMARY}
                size={ComponentSizes.SMALL}
                disabled={!noteContent.trim() || createTextNoteMutation.isPending}
              >
                {createTextNoteMutation.isPending ? 'Creating...' : 'Create Note'}
              </Button>
            </FormActions>
          </CreateNoteForm>
        )}

        <ExistingNotesList>
          {relatedNotes.map((note) => (
            <NoteItem key={note.id}>
              <NoteHeader>
                <NoteIcon $noteType={getNoteType(note)}>{getNoteTypeIcon(note)}</NoteIcon>
                <NoteFileName>{note.fileName}</NoteFileName>
                <NoteDate>{dayjs(note.created).format('MMM D, YYYY h:mm A')}</NoteDate>
              </NoteHeader>
              {note.description && <NoteContent>{note.description}</NoteContent>}
            </NoteItem>
          ))}
        </ExistingNotesList>
      </NotesContainer>
    </Accordion>
  );
};
